/**
 * PortalContentSettings - Content and contact settings for Customer Portal
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Settings, ArrowLeft, Save, Loader2, Phone, Mail, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PortalContentSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    contact_phone: '',
    contact_email: '',
    address: '',
    booking_notice: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/portal/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Load settings error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/portal/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      <Button variant="ghost" className="mb-4 -ml-2" onClick={() => navigate('/portal-settings')}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
      </Button>
      
      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-6">
        <Settings className="h-6 w-6 text-purple-500" /> Inhalt & Kontakt
      </h1>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Kontaktdaten</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="flex items-center gap-2">
                <Phone className="h-4 w-4" /> Telefon
              </Label>
              <Input value={settings.contact_phone || ''} onChange={(e) => setSettings({...settings, contact_phone: e.target.value})} placeholder="+41 ..." className="mt-1" />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Mail className="h-4 w-4" /> Email
              </Label>
              <Input type="email" value={settings.contact_email || ''} onChange={(e) => setSettings({...settings, contact_email: e.target.value})} placeholder="info@..." className="mt-1" />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4" /> Adresse
              </Label>
              <Textarea value={settings.address || ''} onChange={(e) => setSettings({...settings, address: e.target.value})} placeholder="Strasse, PLZ Ort" className="mt-1" rows={2} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Buchungshinweis</CardTitle>
          </CardHeader>
          <CardContent>
            <Label>Hinweistext bei Buchungen</Label>
            <Textarea 
              value={settings.booking_notice || ''} 
              onChange={(e) => setSettings({...settings, booking_notice: e.target.value})} 
              placeholder="Bitte beachten Sie unsere Stornierungsbedingungen..." 
              className="mt-1" 
              rows={4}
            />
            <p className="text-xs text-gray-500 mt-2">Wird bei jeder Buchung im Portal angezeigt</p>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/portal-settings')}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PortalContentSettings;
